@let isMobile = isMobile$ | async;

<div class="sidebar-container">
	<p-drawer
		[(visible)]="sidebarVisible"
		[modal]="false"
		[showCloseIcon]="false"
		position="left"
		[style]="{ width: '20rem', marginTop: '4.6875rem', height: 'calc(100vh - 4.6875rem)' }"
		styleClass="sidebar-drawer"
	>
		<!-- Close button positioned at the edge -->
		<p-button icon="pi pi-times" [text]="true" severity="secondary" (onClick)="toggleSidebar()" class="close-button" [attr.aria-label]="'Close sidebar'" data-testid="sidebar-close-button" />

		<div class="sidebar-content">
			<!-- Mobile View: Navigation and Environment-->
			@if (isMobile) {
				<p-menu [model]="navigationMenuItems()" styleClass="mobile-nav-menu" data-testid="sidebar-nav-menu" />
				<div class="divider"></div>

				<h3 class="section-title">Environment</h3>
				<p-select
					class="env-select"
					[options]="environments()"
					[(ngModel)]="selectedEnvironment"
					optionLabel="label"
					placeholder="Select Environment"
					[style]="{ width: '100%' }"
					data-testid="sidebar-environment-select"
				/>
				<div class="divider"></div>
			}

			@if (!isResultsEnvironment()) {
				<!-- Manual Import -->
				<app-manual-import [hasEnvironment]="hasEnvironment()" />
				<div class="divider"></div>
			} @else {
				<!-- Group Filters (Results environment only) -->
				<app-group-filter />
				<div class="divider"></div>
			}

			<!-- Test Result Search Section -->
			<div class="section-title-row">
				<h3 class="section-title">Test Result Search</h3>
				<div class="section-title-actions">
					<p-button
						icon="pi pi-refresh"
						[rounded]="true"
						[text]="true"
						[outlined]="true"
						severity="secondary"
						size="small"
						(onClick)="refetchDashboardData()"
						[disabled]="!hasEnvironment()"
						pTooltip="Refresh data for the selected date range"
						tooltipPosition="top"
						class="time-toggle-button"
						data-testid="sidebar-refresh-button"
					/>
					<p-button
						icon="pi pi-clock"
						[rounded]="true"
						[text]="true"
						[outlined]="!showTimeSelection()"
						severity="secondary"
						size="small"
						(onClick)="toggleTimeSelection()"
						[pTooltip]="showTimeSelection() ? 'Hide time selection' : 'Show time selection'"
						tooltipPosition="top"
						class="time-toggle-button"
						data-testid="sidebar-time-selection-toggle"
					/>
				</div>
			</div>

			<!-- Date Range Section -->
			<div class="search-section-container">
				<app-search #searchComponent [hasEnvironment]="hasEnvironment()" (dateRangeChange)="onDateRangeChange($event)" />
			</div>

			<div class="divider"></div>

			@if (!isResultsEnvironment() && !isEmuEnvironment()) {
				<!-- Property Filters -->
				@if (hasAnyVisibleFilters()) {
					<h3 class="section-title filter-title">Filter</h3>
				}

				<div class="filter-panels" [class.filters-updating]="(dashboardLoading$ | async) && hasActivePropertyFilter()">
					<!-- DTC filter: cascading ECU → code (exact "code on this ECU" pairing) -->
					@if (hasDtcFilter()) {
						<div class="filter-panel dtc-filter-panel">
							<div
								class="filter-panel-header"
								(click)="dtcExpanded.set(!dtcExpanded())"
								(keydown.enter)="dtcExpanded.set(!dtcExpanded())"
								tabindex="0"
								role="button"
								data-testid="dtc-filter-panel-toggle"
							>
								<span class="filter-panel-title">DTC Filter</span>
								<i [class]="dtcExpanded() ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"></i>
							</div>
							@if (dtcExpanded()) {
								<div class="filter-panel-content">
									<app-dtc-filter [pairs]="dtcPairOptions()" [selectedPair]="pendingDtcPair()" (pairChange)="setDtcPair($event)" />
								</div>
							}
						</div>
					}

					<!-- Main Suite Property Filters -->
					@if (visiblePropertyKeysByLevel().ms.length > 0) {
						<app-property-filter-panel
							[title]="mainSuitePropertiesTitle()"
							[keys]="visiblePropertyKeysByLevel().ms"
							[filters]="pendingFilters()"
							[optionsFn]="getMsOptions"
							level="ms"
							[(expanded)]="msExpanded"
							(filterChange)="setPropertyFilter($event.key, $event.value)"
						/>
					}

					<!-- Test Suite Property Filters -->
					@if (visiblePropertyKeysByLevel().ss.length > 0) {
						<app-property-filter-panel
							[title]="subSuitePropertiesTitle()"
							[keys]="visiblePropertyKeysByLevel().ss"
							[filters]="pendingFilters()"
							[optionsFn]="getSsOptions"
							level="ss"
							[(expanded)]="ssExpanded"
							(filterChange)="setPropertyFilter($event.key, $event.value)"
						/>
					}

					<!-- Test Case Property Filters -->
					@if (visiblePropertyKeysByLevel().tc.length > 0) {
						<app-property-filter-panel
							[title]="testCasePropertiesTitle()"
							[keys]="visiblePropertyKeysByLevel().tc"
							[filters]="pendingFilters()"
							[optionsFn]="getTcOptions"
							level="tc"
							[(expanded)]="tcExpanded"
							(filterChange)="setPropertyFilter($event.key, $event.value)"
						/>
					}
				</div>

				<!-- Filter actions -->
				@if (showApply() || hasPendingFilter() || hasActivePropertyFilter()) {
					<div class="filter-panel filter-actions-row">
						@if (hasPendingFilter() || hasActivePropertyFilter()) {
							<p-button
								label="Clear all"
								[text]="true"
								severity="secondary"
								size="small"
								icon="pi pi-times"
								styleClass="filter-action-clear"
								pTooltip="Clear all filters"
								tooltipPosition="top"
								(onClick)="clearPropertyFilters()"
								data-testid="sidebar-clear-filters-button"
							/>
						}
						@if (showApply()) {
							<p-button
								label="Apply"
								severity="primary"
								size="small"
								icon="pi pi-check"
								styleClass="filter-action-apply"
								[pTooltip]="hasPendingFilter() || hasActivePropertyFilter() ? 'Apply Date Range and Filters' : 'Apply Date Range'"
								tooltipPosition="top"
								(onClick)="applyPendingFilters()"
								data-testid="sidebar-apply-filters-button"
							/>
						}
					</div>
				}

				@if (hasAnyVisibleFilters()) {
					<div class="divider"></div>
				}
			}

			<!-- Text Search Section -->
			@if (!isResultsEnvironment()) {
				<h3 class="section-title search-text-title">Text Search</h3>
				<div class="search-section-container">
					<app-text-search [hasEnvironment]="hasEnvironment()" (textSearch)="onTextSearch($event)" />

					<!-- Last Search Results Link -->
					@if (hasLastSearch()) {
						<div class="last-search-section">
							<p-button
								label="Search Results"
								icon="pi pi-arrow-right"
								(onClick)="goToLastSearch()"
								[text]="true"
								[style]="{ width: '100%' }"
								styleClass="last-search-button"
								data-testid="sidebar-last-search-button"
							/>
						</div>
					}
				</div>
			}
		</div>
	</p-drawer>

	<!-- Minimized sidebar (visible when sidebar is closed) -->
	@if (!sidebarVisible()) {
		@if (isMobile) {
			<!-- Mobile View: small pull tab -->
			<div class="sidebar-pull-tab" (click)="toggleSidebar()" (keydown.enter)="toggleSidebar()" tabindex="0" role="button" title="Open sidebar" data-testid="sidebar-pull-tab-button">
				<i class="pi pi-chevron-right"></i>
			</div>
		} @else {
			<div class="sidebar-minimized">
				@if (!isResultsEnvironment()) {
					<!-- Manual Import Icon -->
					<div
						class="mini-icon"
						(click)="openSidebarToSection('import')"
						(keydown.enter)="openSidebarToSection('import')"
						tabindex="0"
						role="button"
						title="Manual Import"
						data-testid="sidebar-import-mini-button"
					>
						<i class="pi pi-upload"></i>
					</div>

					<div class="mini-divider"></div>
				}

				@if (hasAnyVisibleFilters()) {
					<!-- Filter Icon -->
					<div
						class="mini-icon"
						(click)="openSidebarToSection('filter')"
						(keydown.enter)="openSidebarToSection('filter')"
						tabindex="0"
						role="button"
						title="Filter by Properties"
						data-testid="sidebar-filter-mini-button"
					>
						<i class="pi pi-filter"></i>
					</div>

					<div class="mini-divider"></div>
				}

				@if (!isResultsEnvironment()) {
					<!-- Search Icon -->
					<div
						class="mini-icon"
						(click)="openSidebarToSection('search')"
						(keydown.enter)="openSidebarToSection('search')"
						tabindex="0"
						role="button"
						title="Test Result Search"
						data-testid="sidebar-search-mini-button"
					>
						<i class="pi pi-search"></i>
					</div>
				}

				<!-- Expand Arrow -->
				<div class="mini-icon expand-arrow" (click)="toggleSidebar()" (keydown.enter)="toggleSidebar()" tabindex="0" role="button" title="Expand sidebar" data-testid="sidebar-expand-button">
					<i class="pi pi-chevron-right"></i>
				</div>
			</div>
		}
	}
</div>
